import { ExpandMore, ListAlt, PointOfSale, Spa, Style, Tune } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { sortBy } from 'lodash';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import uniqid from 'uniqid';
import { Dish } from '../../../../apiTypes';
import AllergenIcon from '../../../../components/AllergenIcon';
import DishComponentList from '../../../../components/DishComponentList';
import EditModal from '../../../../components/EditModal';
import FormPriceField from '../../../../components/forms/FormPriceField';
import FormSelect from '../../../../components/forms/FormSelect';
import FormSwitch from '../../../../components/forms/FormSwitch';
import FormTextField from '../../../../components/forms/FormTextField';
import FormUploadImg from '../../../../components/FormUploadImg';
import { allergens, defaultAllergens, units } from '../../../../constants/menu';
import { StyledPaper } from '../../../../constants/styles';
import { t } from '../../../../constants/translations';
import { useAdmin } from '../../../../contexts/admin.context';
import { useCafe } from '../../../../contexts/cafe.context';
import { useLocale } from '../../../../contexts/locale.context';
import { deleteDoc, setDoc, updateDoc } from '../../../../utils/api';
import currency from '../../../../utils/currency';
import { getComponentCost } from '../../../../utils/getPriceByUnit';
import useMedia from '../../../../utils/useMedia';
import EditKitchen from '../../../Kitchen/EditKitchen';
import EditStore from '../../../Stores/EditStore';
import EditCostsOfProducts from '../EditCostsOfProducts';
import VariationsField from '../VariationsField';
import EditDishTaxInfo from './EditDishTaxInfo';

type Props = {
  open: boolean;
  dish?: DbDocument<Dish> | null;
  onClose: () => void;
  categoryId: string;
  lastIndex: number;
};

const EditDish = ({ dish, open, categoryId, onClose, lastIndex }: Props) => {
  const { cafeSlug, cafe } = useAdmin();
  const { lang } = useLocale();
  const { isMobile } = useMedia();
  const { productsMap, semiProductsMap, categories, sectionsMap, kitchens, kitchensMap, legalEntities } = useCafe();
  const [openAddStore, setOpenAddStore] = useState(false);
  const [openAddKitchen, setOpenAddKitchen] = useState(false);
  const [openEditCostsOfProducts, setOpenEditCostsOfProducts] = useState(false);

  const categoriesOptions = useMemo(() => {
    const options = categories.map((c) => ({
      id: c.id,
      name: c.name,
      sectionId: c.sectionId,
      sectionName: sectionsMap[c.sectionId]?.name || '',
    }));

    return sortBy(options, ['sectionName', 'name']);
  }, [categories, sectionsMap]);

  const [selectedCategory, setSelectedCategory] = useState(categoriesOptions.find((o) => o.id === categoryId));

  const form = useForm<Dish>({
    mode: 'onChange',
    defaultValues: {
      name: dish?.name || '',
      description: dish?.description || '',
      imageUrl: dish?.imageUrl || '',
      categoryId,
      price: dish?.price || 0,
      show: dish?.show !== false,
      order: dish?.order || lastIndex + 1,
      time: dish?.time || 0,
      components: dish?.components || [],
      kitchenId: dish?.kitchenId || '',
      variations: dish?.variations || null,
      modifications: dish?.modifications || null,
      volume: dish?.volume || '',
      allergens: dish?.allergens || defaultAllergens,
      vegetarian: dish?.vegetarian || false,
      spicy: dish?.spicy || false,
      byWeight: dish?.byWeight || false,
      unit: dish?.unit || 'g',
      defaultWeight: dish?.defaultWeight || '100',
      taxInfo:
        dish?.taxInfo ||
        (legalEntities[0]
          ? { legalEntityId: legalEntities[0]?.id, uktzed: '', checboxTaxCodes: [], hasExcise: false, barcode: '' }
          : null),
    },
  });

  const byWeight = form.watch('byWeight');
  const unit = form.watch('unit');
  const components = form.watch('components');
  const variations = form.watch('variations');
  const price = form.watch('price');
  const requiredTotalCost = components.reduce((acc, c) => acc + getComponentCost(c, productsMap, semiProductsMap), 0);

  const totalCosts =
    variations && variations.length > 0
      ? variations?.map(
          (variation) =>
            requiredTotalCost +
            variation.components.reduce((acc, c) => acc + getComponentCost(c, productsMap, semiProductsMap), 0)
        )
      : [requiredTotalCost];

  const costPrice = totalCosts
    .map(
      (totalCost) => `${currency(totalCost, cafe?.currency, true)} (${Math.round((totalCost / Number(price)) * 100)}%)`
    )
    .join(' / ');

  const handleClose = () => {
    onClose();
    form.reset();
  };

  const saveChanges = (newDish: Dish) => {
    if (dish?.id) {
      updateDoc<Dish>(`cafes/${cafeSlug}/dishes/${dish.id}`, newDish);
    } else {
      setDoc<Dish>(`cafes/${cafeSlug}/dishes/${uniqid()}`, newDish);
    }

    handleClose();
  };

  const removeProduct = async () => {
    if (dish?.id) {
      // stores.forEach((store) => {
      //   updateDoc(doc(db, "company", companySlug, "stores", store.id), {
      //     [`residues.${consumable.id}`]: deleteField(),
      //   });
      // });
      deleteDoc(`cafes/${cafeSlug}/dishes/${dish.id}`);
      handleClose();
    }
  };

  return (
    <>
      <EditModal<Dish>
        title={t.menu_item[lang]}
        open={open}
        onClose={handleClose}
        submitButton={dish ? t.save_changes[lang] : t.add[lang]}
        onSubmit={saveChanges}
        secondaryButton={dish ? t.remove[lang] : undefined}
        confirmText={t.remove_dish_confirm[lang]}
        onSecondaryButtonClick={removeProduct}
        form={form}
        size="lg"
      >
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 3 }}>
          <Box sx={{ width: isMobile ? 'auto' : 600 }}>
            <Box sx={{ mb: 3, mr: 2 }}>
              <FormUploadImg name="imageUrl" folder="dishes" />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                <FormTextField name="name" label={t.name[lang]} rules={{ required: true }} fullWidth />
              </Grid>

              <Grid item xs={12} sm={12}>
                <FormTextField name="description" multiline rows={3} label={t.description[lang]} fullWidth />
              </Grid>

              <Grid item xs={12} sm={12}>
                <Autocomplete
                  id="categoryId"
                  options={categoriesOptions}
                  onChange={(_: unknown, newValue: typeof categoriesOptions[0]) => {
                    form.setValue('categoryId', newValue.id);
                    setSelectedCategory(newValue);
                  }}
                  value={selectedCategory}
                  disableClearable
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  PaperComponent={StyledPaper}
                  groupBy={(o) => o.sectionName}
                  renderOption={(props, v) => <MenuItem {...props}>{v.name}</MenuItem>}
                  getOptionLabel={(o) => o.name}
                  renderInput={(params) => (
                    <TextField
                      color="info"
                      required
                      label={t.category[lang]}
                      sx={{
                        div: {
                          backgroundColor: (t) => t.palette.background.paper,
                        },
                      }}
                      {...params}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={8}>
                <FormSwitch checkbox name="byWeight" label={t.sold_by_weight_volume[lang]} />
              </Grid>

              <Grid item xs={12} sm={4}>
                {byWeight && (
                  <FormSelect
                    label={t.unit[lang]}
                    size="small"
                    name="unit"
                    options={units}
                    renderOption={(o) => t[o][lang]}
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormPriceField
                  name="price"
                  fullWidth
                  label={t.price[lang]}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{cafe?.currency}</InputAdornment>,
                  }}
                  helperText={`${t.cost_price[lang]}: ${costPrice}`}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                {!byWeight && (
                  <FormTextField
                    name="volume"
                    fullWidth
                    label={`${t.volume[lang]}/${t.weight[lang]}`}
                    rules={{
                      maxLength: 20,
                    }}
                  />
                )}

                {byWeight && (
                  <FormTextField
                    name="defaultWeight"
                    type="number"
                    label={t.standard_portion[lang]}
                    fullWidth
                    rules={{ required: true, min: 1, pattern: /^[0-9]{0,5}$/ }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{t[unit || 'g'][lang]}</InputAdornment>,
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ width: isMobile ? 'auto' : 600 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={12}>
                <FormSwitch name="show" label={t.show_for_visitors[lang]} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormSelect
                  name="kitchenId"
                  fullWidth
                  options={kitchens.map((w) => w.id)}
                  label={t.cooking_place[lang]}
                  renderOption={(o) => kitchensMap[o]?.name}
                  onAddNew={() => setOpenAddKitchen(true)}
                  addButtonText={t.add_cooking_place[lang]}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormTextField
                  name="time"
                  type="number"
                  fullWidth
                  label={t.cooking_time[lang]}
                  rules={{
                    min: 0,
                    valueAsNumber: true,
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{t.minutes_short[lang]}</InputAdornment>,
                    sx: { bgcolor: (t) => t.palette.background.paper },
                  }}
                />
              </Grid>
            </Grid>
            <div>
              <Accordion
                elevation={0}
                sx={{ border: (t) => `1px solid ${t.palette.grey[400]}`, '::before': { opacity: 0 } }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography sx={{ color: (t) => t.palette.text.secondary }}>
                    <ListAlt sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                    {t.costs_of_production[lang]}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <DishComponentList components={components} onEdit={() => setOpenEditCostsOfProducts(true)} />
                </AccordionDetails>
              </Accordion>

              <Accordion
                elevation={0}
                sx={{ border: (t) => `1px solid ${t.palette.grey[400]}`, '::before': { opacity: 0 } }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography sx={{ color: (t) => t.palette.text.secondary }}>
                    <Tune sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                    {t.modifications[lang]}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <VariationsField
                    requiredComponents={components}
                    type="modifications"
                    form={form}
                    price={Number(price)}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                elevation={0}
                sx={{ border: (t) => `1px solid ${t.palette.grey[400]}`, '::before': { opacity: 0 } }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography sx={{ color: (t) => t.palette.text.secondary }}>
                    <Style sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                    {t.variations[lang]}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <VariationsField
                    requiredComponents={components}
                    type="variations"
                    form={form}
                    price={Number(price)}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                elevation={0}
                sx={{ border: (t) => `1px solid ${t.palette.grey[400]}`, '::before': { opacity: 0 } }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography sx={{ color: (t) => t.palette.text.secondary }}>
                    <Spa sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                    {t.allergens[lang]}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    {allergens.map((allergen) => (
                      <FormSwitch
                        key={allergen}
                        checkbox
                        name={`allergens.${allergen}`}
                        label={
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <AllergenIcon name={allergen} />
                            <Typography variant="overline" sx={{ lineHeight: 2.4 }}>
                              {t[allergen][lang]}
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>

              {legalEntities.length > 0 && (
                <Accordion
                  elevation={0}
                  sx={{ border: (t) => `1px solid ${t.palette.grey[400]}`, '::before': { opacity: 0 } }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography sx={{ color: (t) => t.palette.text.secondary }}>
                      <PointOfSale sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                      {t.tax_information[lang]}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <EditDishTaxInfo />
                  </AccordionDetails>
                </Accordion>
              )}
            </div>

            <Box sx={{ my: 2 }}>
              <FormSwitch checkbox name="vegetarian" label={t.vegetarian[lang]} />

              <FormSwitch checkbox name="spicy" label={t.spicy[lang]} />
            </Box>
          </Box>
        </Box>
      </EditModal>

      {openAddStore && <EditStore open onClose={() => setOpenAddStore(false)} />}
      {openAddKitchen && <EditKitchen open onClose={() => setOpenAddKitchen(false)} />}
      {openEditCostsOfProducts && (
        <EditCostsOfProducts
          open
          defaultComponents={components}
          onSubmit={(v) => {
            form.setValue('components', v);
          }}
          onClose={() => setOpenEditCostsOfProducts(false)}
          price={Number(price)}
        />
      )}
    </>
  );
};

export default EditDish;
